import ActionButton from "@/components/buttons/ActionButton";
import { ContactsView } from "@/components/contact/ContactsView";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import HtmlView from "@/components/views/HtmlView";
import { formatAddress, formatIban, formatLocaleDate, formatPhoneNumber } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { Button, Card, Col, Descriptions, Flex, Popconfirm, Row, Tag } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import { useDeleteAgent, useGetAgentEnumerations } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { hasPermission } from "../../../../common/security/utils";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { useCurrentAgentAccount, useIsCurrentAgentTypePlus, usePermissions } from "../../../auth/hooks";
import { refreshSession } from "../../../auth/session";
import { AgentStatus, AgentType } from "../../enums";
import { AGENT_ROUTE_PATHS } from "../../paths";
import { Agent } from "../../types";
import {
  formatAgentDirectorateName,
  formatAgentName,
  formatAgentSystemIdNumber,
  isAgentTipper,
  isLegalAgent,
  isNaturalAgent,
  isSelfEmployedAgent,
} from "../../utils";
import { AgentProfilePictureEditor } from "../AgentProfilePictureEditor";
import { AgentParentModalForm } from "../forms/AgentParentModalForm";
import AgentActiveTag from "../tags/AgentActiveTag";
import AgentCanBeGainerTag from "../tags/AgentCanBeGainerTag";
import { AgentTipperTag } from "../tags/AgentTipperTag";
import { PersonalData } from "./PersonalData";

const DESCRIPTION_LABEL_WIDTH = 210;

interface Props {
  agent: Agent;
  onUpdateClick: () => void;
}

export const AgentDetailView = ({ agent, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [isAgentParentModalOpen, setIsAgentParentModalOpen] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const userAccount = useCurrentAgentAccount();
  const permissions = usePermissions();

  const { data } = useGetAgentEnumerations(agent.parent?.id ? [agent.parent?.id] : []);
  const agentParentEnum = data?.[0];

  const deleteAgent = useDeleteAgent();

  const hiddenUpdateParent =
    agent.id === agent.accessTreePath ||
    agent.status === AgentStatus.BROKER_REPRESENTATIVE ||
    (isNaturalAgent(agent) && agent.representedAgents.length > 0);

  const items = useMemo(() => {
    const items: DescriptionsItemType[] = [];

    if (!isNaturalAgent(agent)) {
      items.push({
        label: t("agent.attrs.companyName"),
        children: agent.companyName,
      });
    } else {
      items.push({
        label: t("agent.attrs.aggregatedName"),
        children: agent.aggregatedName,
      });
    }

    items.push({
      label: t("agent.titles.structuralData"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: t("agent.attrs.systemIdNumber"),
              children: <CopyToClipboardView content={formatAgentSystemIdNumber(agent)} />,
            },
            {
              label: t("agent.attrs.partnerIdNumber"),
              children: <CopyToClipboardView content={agent.partnerIdNumber} />,
            },
            {
              label: t("agent.attrs.directorate"),
              children: agent.directorate ? formatAgentDirectorateName(agent.directorate) : undefined,
            },
            {
              label: t("agent.attrs.parentId"),
              children: (
                <Flex justify="space-between">
                  <div>{formatAgentName(agent.parent)}</div>

                  {!hiddenUpdateParent && (
                    <div>
                      <ActionButton
                        className="margin-left-tiny"
                        icon="edit"
                        variant="solid"
                        color="primary"
                        onClick={() => setIsAgentParentModalOpen(true)}
                      >
                        {t("common.edit")}
                      </ActionButton>
                    </div>
                  )}
                </Flex>
              ),
            },
            agentParentEnum?.representatives?.length && {
              label: t("agent.helpers.parentRepresentatives"),
              children: agentParentEnum.representatives
                .map((representative) => formatAgentName(representative))
                .join(", "),
            },
          ].filter((item) => !!item)}
        />
      ),
    });

    if (isNaturalAgent(agent)) {
      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={agent} width={DESCRIPTION_LABEL_WIDTH} />,
      });

      items.push({
        label: t("agent.helpers.representativeFor"),
        children: agent.representedAgents.map((agent, index) => (
          <Fragment key={agent.id}>
            {index > 0 && ", "}
            <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })} target="_blank">
              {formatAgentName(agent)}
            </Link>
          </Fragment>
        )),
      });
    } else if (isSelfEmployedAgent(agent)) {
      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={agent} width={DESCRIPTION_LABEL_WIDTH} />,
      });

      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 170 } }}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={agent.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.districtOffice"),
                children: agent.districtOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={agent.taxId} />,
              },
              {
                label: t("agent.attrs.tradeRegisterNumber"),
                children: agent.tradeRegisterNumber,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={agent.vatId} />,
              },
            ]}
          />
        ),
      });
    } else if (isLegalAgent(agent)) {
      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 170 } }}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={agent.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.commercialRegisterDistrictOffice"),
                children: agent.commercialRegisterDistrictOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={agent.taxId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterSection"),
                children: agent.commercialRegisterSection,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={agent.vatId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterInsertionNumber"),
                children: agent.commercialRegisterInsertionNumber,
              },
              {
                label: t("agent.attrs.legalTypeChangeDate"),
                children: agent.legalTypeChangeDate ? formatLocaleDate(agent.legalTypeChangeDate) : undefined,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("agent.attrs.representatives"),
        children: agent.representatives.map((agent, index) => (
          <Fragment key={agent.id}>
            {index > 0 && ", "}
            <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })} target="_blank">
              {formatAgentName(agent)}
            </Link>
          </Fragment>
        )),
      });
    }

    items.push({
      label: t("agent.titles.scope"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: "Zmluvné vzťahy",
              children: (
                <div>
                  {agent.status && (
                    <div>
                      <Descriptions
                        size="small"
                        bordered
                        styles={{ label: { width: 140 } }}
                        column={1}
                        items={[
                          {
                            label: isTopAgentPlus
                              ? t("agent.helpers.agentStatusSFAPlusShortLabel")
                              : t("agent.enums.agentStatus._label"),
                            children: agent.status ? t(`agent.enums.agentStatus.${agent.status}`) : undefined,
                          },
                          {
                            label: t("common.date"),
                            children: `${agent.statusStartDate ? `${t("common.from")} ${formatLocaleDate(agent.statusStartDate)}` : ""} ${agent.statusStartDate && agent.statusEndDate ? "-" : ""} ${agent.statusEndDate ? `${t("common.to")} ${formatLocaleDate(agent.statusEndDate)}` : ""}`,
                          },
                        ]}
                      />
                    </div>
                  )}

                  {isTopAgentPlus && (
                    <div>
                      <Descriptions
                        className="margin-top-small"
                        size="small"
                        bordered
                        column={1}
                        styles={{ label: { width: 140 } }}
                        items={[
                          {
                            label: t("commissions.batch.enums.commissionBatchType_PLUS.SECONDARY"),
                            children: agent.secondaryStatus
                              ? t(`agent.enums.agentSecondaryStatus.${agent.secondaryStatus}`)
                              : undefined,
                          },
                          {
                            label: t("common.date"),
                            children: `${agent.secondaryStatusStartDate ? `${t("common.from")} ${formatLocaleDate(agent.secondaryStatusStartDate)}` : ""} ${agent.secondaryStatusStartDate && agent.secondaryStatusEndDate ? "-" : ""} ${agent.secondaryStatusEndDate ? `${t("common.to")} ${formatLocaleDate(agent.secondaryStatusEndDate)}` : ""}`,
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              ),
            },
            {
              label: t("agent.attrs.realtySpecialist"),
              children: tBoolean(agent.realtySpecialist),
            },
            {
              label: t("agent.attrs.loanSpecialist"),
              children: tBoolean(agent.loanSpecialist),
            },
            {
              label: t("common.financialSectors"),
              children: agent.sectors.map((sector) => t("agent.enums.financialSector." + sector)).join(", "),
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.basicData"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: t("agent.attrs.email"),
              children: (
                <CopyToClipboardView
                  content={agent.email}
                  wrapper={<a href={`mailto:${agent.email}`}>{agent.email}</a>}
                />
              ),
            },
            {
              label: t("agent.attrs.phone"),
              children: (
                <CopyToClipboardView
                  content={agent.phone}
                  wrapper={<a href={`tel:${agent.phone}`}>{formatPhoneNumber(agent.phone)}</a>}
                />
              ),
            },
            {
              label: t("agent.attrs.web"),
              children: agent.web,
            },
            {
              label: t("agent.attrs.bankAccountNumber"),
              children: (
                <CopyToClipboardView
                  content={agent.bankAccountNumber ? formatIban(agent.bankAccountNumber) : undefined}
                />
              ),
            },
            {
              label: t("agent.attrs.nbsRegistrationNumber"),
              children: <CopyToClipboardView content={agent.nbsRegistrationNumber} />,
            },
            {
              label: t("agent.attrs.note"),
              children: agent.note ? <HtmlView value={agent.note} /> : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.addresses"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: agent.type === AgentType.LEGAL ? t("agent.attrs.addressLongPO") : t("agent.attrs.addressLong"),
              children: formatAddress(agent.address),
            },
            {
              label:
                agent.type === AgentType.SELF_EMPLOYED
                  ? t("agent.attrs.serviceAddressFOP")
                  : t("agent.attrs.serviceAddress"),
              children: agent.serviceAddress ? formatAddress(agent.serviceAddress) : undefined,
            },
            {
              label: t("agent.attrs.correspondenceAddress"),
              children: agent.correspondenceAddress ? formatAddress(agent.correspondenceAddress) : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.contacts"),
      children: <ContactsView contacts={agent.contacts} />,
    });

    return items;
  }, [agent, agentParentEnum, hiddenUpdateParent, isTopAgentPlus]);

  return (
    <>
      <div className="margin-top-medium">
        {isAgentTipper(agent) ? <AgentTipperTag /> : undefined}
        <AgentCanBeGainerTag canBeGainer={agent.canBeGainer} />
        <AgentActiveTag agentDeactivated={agent.deactivated} />
        {(isLegalAgent(agent) || isSelfEmployedAgent(agent)) && agent.vatId && (
          <Tag color="blue">{t("agent.helpers.vatPayer")}</Tag>
        )}
      </div>

      <Row className="margin-top-tiny" gutter={24}>
        <Col span={18}>
          <Descriptions styles={{ label: { fontWeight: "bold", minWidth: 180 } }} column={1} bordered items={items} />
        </Col>

        <Col span={6}>
          <Card>
            <Flex justify="center">
              <AgentProfilePictureEditor
                className="left-align"
                agent={agent}
                width={200}
                showActions={hasPermission(permissions, Permission.AGENT_UPDATE)}
                updateProfilePictureCallback={() => {
                  // The header shows the current agent's profile picture.
                  if (userAccount?.agent.id === agent.id || userAccount?.representingAgent?.id === agent.id) {
                    refreshSession();
                  }
                }}
              />
            </Flex>
          </Card>
        </Col>
      </Row>

      <div className="margin-top-large">
        <ComponentWithPermission permissions={[Permission.AGENT_UPDATE]}>
          <Button className="margin-right-tiny" type="primary" onClick={onUpdateClick} icon={<AntIcon type="edit" />}>
            {t("common.edit")}
          </Button>
        </ComponentWithPermission>

        <ComponentWithPermission permissions={[Permission.AGENT_DELETE]}>
          <Popconfirm
            title={t("agent.titles.deleteConfirm", { aggregatedName: agent.aggregatedName })}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => {
              deleteAgent.mutate(agent.id);

              navigate(generatePath(AGENT_ROUTE_PATHS.list.to));
            }}
          >
            <Button icon={<AntIcon type="delete" />} danger>
              {t("common.delete")}
            </Button>
          </Popconfirm>
        </ComponentWithPermission>
      </div>

      <AgentParentModalForm
        isOpen={isAgentParentModalOpen}
        agent={agent}
        onCancel={() => setIsAgentParentModalOpen(false)}
      />
    </>
  );
};
